import React from "react";
import AlertIcon from "@mui/icons-material/WarningAmberOutlined";
import TrendIcon from "@mui/icons-material/TrendingUp";
import HeartIcon from "@mui/icons-material/MonitorHeartOutlined";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import CertIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ClockIcon from "@mui/icons-material/AccessTime";
import InboxIcon from "@mui/icons-material/InboxOutlined";
import PublicIcon from "@mui/icons-material/Public";
import HistoryIcon from "@mui/icons-material/History";
import { Columns, LineChart, Num, useReady } from "./adminCharts";
import { CATEGORIES } from "./categories";
import { ROUTES } from "../shared/classData";
import { ActionList, LoadList } from "../widgets/commonBodies";
import { ActivityBody, ApprovalsBody, TenantsBody } from "./adminDataWidgets";
import { GLOBAL_ADMIN_PATHS } from "../../../pages/globalAdmin/paths";

export const RANGE_WINDOWS = { "30D": 1, "90D": 3, "12M": 12 };
const HEALTH_COLORS = { ok: "var(--color-success)", risk: "var(--color-warn)", behind: "var(--color-danger)" };

function Legend({ items }) {
  return <div className="legend">{items.map(([name, color]) => <span key={name}><i style={{ background: color }}></i>{name}</span>)}</div>;
}

function ActionsBody({ ctx }) {
  return <ActionList actions={ctx.m.actions} />;
}

function TrendBody({ ctx }) {
  const { trend } = ctx.m;
  const win = RANGE_WINDOWS[ctx.range] || 12;
  const last = (arr) => arr.slice(-win);
  const labels = last(trend.labels);
  if (labels.length < 2) return <p className="wg-empty">Not enough class history to chart yet.</p>;
  return (
    <LineChart labels={labels} series={[
      { name: "Enrolled", color: "var(--color-accent-violet)", data: last(trend.enrolled) },
      ...(trend.completed ? [{ name: "Completed", color: "var(--color-cat-tactical)", data: last(trend.completed) }] : [])]} />
  );
}

function HealthBody({ ctx }) {
  const ready = useReady(250);
  const { health } = ctx.m;
  return (
    <div style={{ display: "grid", gap: 14 }} data-testid="admin-program-health">
      <div className="wst">{[["ok", "On track"], ["risk", "At risk"], ["behind", "Behind"]].map(([k, label]) => (
        <div key={k}><b><Num to={health.counts[k]} delay={300} /></b><span><i style={{ width: 8, height: 8, borderRadius: "50%", background: HEALTH_COLORS[k] }}></i>{label}</span></div>))}</div>
      <div aria-hidden="true" style={{ display: "flex", gap: 2, height: 12, borderRadius: 6, overflow: "hidden", background: "var(--color-surface-press-light)" }}>
        {["ok", "risk", "behind"].map((k) => <i key={k} style={{ flexGrow: ready ? health.counts[k] : 0, background: HEALTH_COLORS[k], transition: "flex-grow 1s cubic-bezier(.3,.7,.2,1)" }}></i>)}</div>
      {health.rows.length ? <div className="wl">{health.rows.map((h) => (
        <div key={h.id} className="wr"><div><b style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="dot" style={{ background: HEALTH_COLORS[h.status] }}></span>{h.name}</b><small style={{ paddingLeft: 16 }}>{h.sub}</small></div>
          <span className="mo" style={{ fontWeight: 600, color: h.status === "ok" ? undefined : HEALTH_COLORS[h.status] }}>{h.pct}%</span></div>))}</div>
        : <p className="wg-empty">No active cohorts.</p>}
    </div>
  );
}

function StartsBody({ ctx }) {
  const ready = useReady(250);
  const { upcoming } = ctx.m;
  if (!upcoming.length) return <p className="wg-empty">No upcoming cohort starts.</p>;
  return (
    <div className="wl">{upcoming.map((u) => {
      const full = u.total > 0 && u.filled >= u.total;
      const pct = u.total ? Math.min(100, u.filled / u.total * 100) : 0;
      return (
        <div key={u.id} style={{ display: "grid", gridTemplateColumns: "48px minmax(0,1fr)", gap: 12, alignItems: "center" }}>
          <div style={{ display: "grid", textAlign: "center", border: "1px solid var(--color-hairline-cloud)", borderRadius: "var(--radius-md)", padding: "5px 0" }}>
            <small style={{ font: "700 9.5px var(--font-ui)", letterSpacing: ".1em", textTransform: "uppercase", opacity: 0.6, display: "block" }}>{u.month}</small><b style={{ font: "600 18px/1.1 var(--font-display)" }}>{u.day}</b></div>
          <div><b style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="dot" style={{ background: u.color }}></span>{u.title}</b>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "center", marginTop: 6, fontSize: 12 }}>
              <div className="pb"><i className={full ? "full" : ""} style={{ width: ready ? `${pct}%` : 0, background: full ? "var(--color-success)" : "var(--color-ink)" }}></i></div>
              <span className="mo" style={{ opacity: 0.7 }}>{full ? "Full" : u.total ? `${u.filled} of ${u.total} seats` : `${u.filled} enrolled`}</span></div></div>
        </div>
      );
    })}</div>
  );
}

function ExpiringBody({ ctx }) {
  return <Columns weeks={ctx.m.expiring} cats={CATEGORIES} />;
}

function LoadBody({ ctx }) {
  return <LoadList rows={ctx.m.load} />;
}

/** Admin widget library. `available` hides global-admin-only widgets from tenant admins. */
export const ADMIN_WIDGETS = {
  actions: { name: "Action needed", group: "Overview", desc: "Expiring credentials, over-capacity instructors, sign-offs, submissions and cohorts without an instructor.", Icon: AlertIcon, sizes: [5, 6, 7, 8, 12], add: 7, count: (ctx) => ctx.m.actions.length || null, isEmpty: (ctx) => ctx.m.actions.length === 0, emptyText: "Nothing needs your attention.", Body: ActionsBody },
  trend: { name: (ctx) => (ctx.m.trend.completed ? "Enrollments and completions" : "Enrollments by class start month"), title: "Enrollments", group: "Programs", desc: "Monthly enrollments across all cohorts; completions appear when classes report them.", Icon: TrendIcon, sizes: [7, 8, 12], aside: (ctx) => <Legend items={[["Enrolled", "var(--color-accent-violet)"], ...(ctx.m.trend.completed ? [["Completed", "var(--color-cat-tactical)"]] : [])]} />, Body: TrendBody },
  health: { name: "Program health", group: "Programs", desc: "Cohorts on track, at risk and behind, judged by instructor assignment and seat fill.", Icon: HeartIcon, sizes: [4, 5, 6], link: { label: "All cohorts", to: ROUTES.catalog }, Body: HealthBody },
  starts: { name: "Upcoming cohort starts", group: "Programs", desc: "Next cohorts to start, with seats filled.", Icon: CalendarIcon, sizes: [4, 5, 6], add: 5, link: { label: "Schedule", to: ROUTES.manageCourses }, Body: StartsBody },
  approvals: { name: "Approvals queue", group: "Programs", desc: "Enrollment requests, instructor sign-ups and course submissions waiting on a decision.", Icon: InboxIcon, sizes: [4, 5, 6], add: 5, link: { label: "Open approvals", to: ROUTES.approvals }, Body: ApprovalsBody },
  expiring: { name: "Instructor certifications expiring · next 12 weeks", title: "Instructor certifications expiring", group: "Compliance", desc: "Weekly instructor certification expirations by category for the next 12 weeks.", Icon: CertIcon, sizes: [6, 7, 8, 12], add: 7, aside: <Legend items={CATEGORIES.map((c) => [c.name, c.color])} />, Body: ExpiringBody },
  load: { name: "Instructor load", group: "Staff", desc: "Scheduled hours per instructor against 40-hour capacity.", Icon: ClockIcon, sizes: [4, 5, 6], add: 5, meta: "This week", Body: LoadBody },
  tenants: { name: "Training centers", group: "System", desc: "Every training center with its custom domain and lock status.", Icon: PublicIcon, sizes: [5, 6, 7, 8, 12], available: (ctx) => ctx.isGlobalAdmin, link: { label: "Manage", to: GLOBAL_ADMIN_PATHS.tenants }, Body: TenantsBody },
  activity: { name: "Audit log", group: "System", desc: "The latest audit-log events from the last day.", Icon: HistoryIcon, sizes: [5, 6, 7, 8, 12], available: (ctx) => ctx.isGlobalAdmin, meta: "Today", link: { label: "Full log", to: GLOBAL_ADMIN_PATHS.auditLog }, Body: ActivityBody },
};

export const ADMIN_DEFAULTS = [["trend", 8], ["health", 4], ["expiring", 7], ["load", 5], ["actions", 7], ["starts", 5]];
